import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, Phone } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const NavMenu = ({ onNavigate }) => {
  const { t } = useI18n();
  const [openSub, setOpenSub] = useState(false);
  const items = [
    { to: "/", label: t.nav.home },
    {
      label: t.nav.services,
      subItems: [
        { to: "/lokale-ki", label: t.nav.local_ai || "Lokale KI" },
        { to: "/managed-services", label: t.nav.managed_services || "Managed Services" },
        { to: "/managed-security", label: t.nav.managed_security },
        { to: "/managed-backup", label: t.nav.managed_backup },
        { to: "/managed-server", label: t.nav.managed_server },
        { to: "/schwachstellenmanagement", label: t.nav.schwachstellen },
        { to: "/managed-wlan", label: t.nav.managed_wlan },
        { to: "/ueberwachungskameras", label: t.nav.cameras || "Überwachungskameras" },
        { to: "/webdesign", label: t.nav.webdesign },
        { to: "/webseiten-check", label: t.nav.website_check || "Webseiten-Check" },
      ],
    },
    { to: "/blog", label: t.nav.blog },
    { to: "/kontakt", label: t.nav.contact },
  ];

  return (
    <ul className="flex flex-col md:flex-row md:items-center gap-2 md:gap-1">
      {items.map((item) => (
        <li key={item.label} className="relative group">
          {item.subItems ? (
            <>
              <button
                data-testid={`nav-${item.label}-toggle`}
                onClick={() => setOpenSub((v) => !v)}
                className="flex items-center gap-1 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-primary transition-colors w-full md:w-auto"
              >
                {item.label}
                <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
              </button>
              <div className={`md:absolute md:top-full md:left-0 md:pt-2 md:opacity-0 md:invisible md:group-hover:opacity-100 md:group-hover:visible transition-all ${openSub ? "block" : "hidden md:block"}`}>
                <div className="bg-white border border-line min-w-[260px] py-2 shadow-soft">
                  {item.subItems.map((s) => (
                    <NavLink
                      key={s.to}
                      to={s.to}
                      onClick={onNavigate}
                      data-testid={`nav-sub-${s.to}`}
                      className={({ isActive }) =>
                        `block px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] border-l-2 ${
                          isActive
                            ? "border-primary text-primary bg-soft"
                            : "border-transparent text-muted hover:text-primary hover:bg-soft"
                        }`
                      }
                    >
                      {s.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <NavLink
              to={item.to}
              onClick={onNavigate}
              data-testid={`nav-${item.to}`}
              className={({ isActive }) =>
                `block px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-primary" : "text-muted hover:text-primary"
                }`
              }
            >
              {item.label}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );
};

export const Header = () => {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-line shadow-soft"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group" aria-label="IT-Servicepoint Startseite">
          <img
            src="/brand/logo-mark.png"
            alt="IT-Servicepoint Logo"
            className="h-10 w-auto"
            width="40"
            height="40"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-black text-lg tracking-tightest text-ink">IT-Servicepoint</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">made in germany</span>
          </div>
        </Link>

        <nav className="hidden md:block" aria-label="Hauptnavigation">
          <NavMenu />
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href="tel:+4966299153890"
            className="hidden lg:inline-flex items-center gap-2 px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-primary transition-colors"
            aria-label="Anrufen"
          >
            <Phone size={14}/> +49 6629 9153890
          </a>
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "de" ? "en" : "de")}
            className="flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-primary border border-line hover:border-primary transition-colors"
            aria-label="Sprache umschalten"
          >
            <Globe size={14} /> {t.common.lang}
          </button>
          <Link
            to="/kontakt"
            data-testid="header-cta"
            className="px-5 py-2.5 bg-primary text-white font-medium hover:bg-primary-hover transition-colors text-sm"
          >
            {t.cta.contact_us}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-ink p-2"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menü"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-line bg-white">
          <div className="px-6 py-6">
            <NavMenu onNavigate={() => setMobileOpen(false)} />
            <div className="mt-6 flex items-center gap-3">
              <button
                data-testid="lang-toggle-mobile"
                onClick={() => setLang(lang === "de" ? "en" : "de")}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-primary border border-line"
              >
                <Globe size={14} /> {t.common.lang}
              </button>
              <Link
                to="/kontakt"
                data-testid="header-cta-mobile"
                className="flex-1 text-center px-5 py-3 bg-primary text-white font-medium hover:bg-primary-hover transition-colors text-sm"
              >
                {t.cta.contact_us}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
